<!--
  ~ Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
  ~ Venelin Iliev <http://veneliniliev.com>
  -->

<div class="container-fluid" *ngIf="accessService.hasAccess([], ['campaign.create'])">
  <div class="row align-items-center">
    <div class="col-auto">
      <h5 class="title-secondary">{{ 'CAMPAIGN.PAGE.TITLE' | translate }}</h5>
    </div>
    <div *ngIf="currentStep === 1" class="col-auto ml-auto">
      <button type="button" class="btn btn-link" (click)="clearFilters()">
        {{ 'CLEAR_FILTERS' | translate }}
      </button>
    </div>
  </div>

  <div class="flex-grow-1" *ngIf="currentStep === 1">
    <div class="top">
      <kendo-grid
        [data]="data"
        [pageSize]="requestsResponse?.meta.per_page"
        [sortable]="true"
        [filterable]="true"
        [columnMenu]="true"
        [filter]="state?.filter"
        [sort]="state?.sort"
        [skip]="state?.skip"
        [pageable]="true"
        (dataStateChange)="loadData($event)"
        [style.height]="'100%'"
        [selectable]="selectableSettings"
        [kendoGridSelectBy]="mySelectionKey"
        [selectedKeys]="selectedSalesIds"
        (selectedKeysChange)="selectedSalesChange($event)"
        [loading]="loader"
        (columnVisibilityChange)="onVisibilityChange($event)"
      >
        <kendo-grid-checkbox-column
          [width]="45"
          showSelectAll="true"
          class="text-center p-1"
          headerClass="text-center p-1"
        >
        </kendo-grid-checkbox-column>

        <kendo-grid-column
          [width]="70"
          class="text-center"
          headerClass="text-center"
          title="{{ 'CAMPAIGN.CAMPAIGN_LIST.NUMBER' | translate }}"
        >
          <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
            {{ rowIndex + 1 }}
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column
          class="text-center"
          headerClass="text-center"
          field="id"
          title="{{ 'CAMPAIGN.CAMPAIGN_LIST.ID' | translate }}"
          filter="numeric"
          [width]="120"
          [hidden]="isHiddenColumn('id')"
        ></kendo-grid-column>

        <kendo-grid-column
          field="client.name"
          title="{{ 'CAMPAIGN.CAMPAIGN_LIST.CLIENT' | translate }}"
          filter="text"
          [hidden]="isHiddenColumn('client.name')"
        >
          <ng-template kendoGridFilterCellTemplate let-filter>
            <app-kendo-client-select [filter]="filter"></app-kendo-client-select>
          </ng-template>

          <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
            <app-client-box [client]="dataItem.client" [flag]="true"></app-client-box>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column
          field="client.city.timezone_diff"
          title="{{ 'CAMPAIGN.CAMPAIGN_LIST.CLIENT_TIME' | translate }}"
          [filterable]="false"
          [hidden]="isHiddenColumn('client.city.timezone_diff', true)"
        >
          <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
            <div
              class="active-time-wrapper"
              *ngIf="dataItem.meta_front?.clientTime"
              [ngbTooltip]="'CAMPAIGN.CAMPAIGN_LIST.CLIENT_TIME' | translate"
              container="body"
            >
              <i class="fal fa-clock"></i> {{ dataItem.meta_front?.clientTime }}
            </div>
            <div class="active-time-wrapper" *ngIf="dataItem.meta_front?.clientTime === 'false'">
              <i class="fas fa-exclamation-triangle"></i>
              {{ 'CAMPAIGN.CAMPAIGN_LIST.CLIENT_TIMEZONE_MISSING' | translate }}
            </div>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column
          field="client.country.name"
          title="{{ 'CAMPAIGN.CAMPAIGN_LIST.CLIENT_COUNTRY' | translate }}"
          [hidden]="isHiddenColumn('client.country.name', true)"
        >
          <ng-template kendoGridFilterCellTemplate let-filter>
            <app-kendo-country-select [filter]="filter" [field]="'client.country_id'"></app-kendo-country-select>
          </ng-template>

          <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
            <app-country-box [country]="dataItem?.client?.country" [flag]="false"></app-country-box>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column
          field="clientTags"
          title="{{ 'CAMPAIGN.CAMPAIGN_LIST.CLIENT_TAGS' | translate }}"
          filter="text"
          [hidden]="isHiddenColumn('clientTags', true)"
          [sortable]="false"
        >
          <ng-template kendoGridFilterCellTemplate let-filter let-column="column">
            <app-kendo-tag-select [filter]="filter" [model]="LaravelModelsEnum.CLIENT" field="clientTags">
            </app-kendo-tag-select>
          </ng-template>

          <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
            <span *ngFor="let tag of dataItem.client?.tags; let i = index">
              {{ tag }}<span *ngIf="i !== dataItem?.client?.tags?.length - 1">, </span>
            </span>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column
          field="event.formattedName"
          title="{{ 'CAMPAIGN.CAMPAIGN_LIST.EVENT' | translate }}"
          filter="text"
          [hidden]="isHiddenColumn('event.formattedName')"
          [sortable]="false"
        >
          <ng-template kendoGridFilterCellTemplate let-filter>
            <app-kendo-event-select [filter]="filter"></app-kendo-event-select>
          </ng-template>

          <ng-template kendoGridCellTemplate let-dataItem>
            <a [routerLink]="['/events/view']" [queryParams]="{ id: dataItem?.event?.id }" target="_blank">
              {{ dataItem?.event?.formattedName }}
            </a>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column
          field="event.event_group.name"
          title="{{ 'CAMPAIGN.CAMPAIGN_LIST.GROUP_NAME' | translate }}"
          filter="text"
          [hidden]="isHiddenColumn('event.event_group.name', true)"
        >
          <ng-template kendoGridFilterCellTemplate let-filter>
            <app-kendo-event-group-select
              [filter]="filter"
              [field]="'event.event_group_id'"
            ></app-kendo-event-group-select>
          </ng-template>

          <ng-template kendoGridCellTemplate let-dataItem>
            <a
              [routerLink]="['/event-groups/view']"
              [queryParams]="{ id: dataItem?.event?.event_group?.id }"
              target="_blank"
            >
              {{ dataItem?.event?.event_group?.name }}
            </a>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column
          field="mail_unactive"
          title="{{ 'CAMPAIGN.CAMPAIGN_LIST.MAIL_UNACTIVE' | translate }}"
          filter="boolean"
          [class]="'text-center'"
          [hidden]="isHiddenColumn('mail_unactive', true)"
        >
          <ng-template kendoGridCellTemplate let-dataItem>
            <app-active-indicator [active]="dataItem.mail_unactive"></app-active-indicator>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column
          field="source.value"
          title="{{ 'CAMPAIGN.CAMPAIGN_LIST.SOURCE' | translate }}"
          filter="text"
          [hidden]="isHiddenColumn('source.value', true)"
        >
          <ng-template kendoGridFilterCellTemplate let-filter>
            <app-kendo-type-select
              [groupType]="'sale_source'"
              [filter]="filter"
              [field]="'source_id'"
            ></app-kendo-type-select>
          </ng-template>

          <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
            <app-type-badge [type]="dataItem.source"></app-type-badge>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column
          field="status.value"
          title="{{ 'CAMPAIGN.CAMPAIGN_LIST.STATUS' | translate }}"
          filter="text"
          [hidden]="isHiddenColumn('status.value')"
        >
          <ng-template kendoGridFilterCellTemplate let-filter>
            <app-kendo-type-select
              [groupType]="'sale_status'"
              [filter]="filter"
              [field]="'status_id'"
            ></app-kendo-type-select>
          </ng-template>

          <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
            <app-type-badge [type]="dataItem.status"></app-type-badge>
            <div class="text-truncate" [ngbTooltip]="dataItem.status_reason" container="body">
              {{ dataItem.status_reason }}
            </div>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column
          field="company.name"
          title="{{ 'CAMPAIGN.CAMPAIGN_LIST.COMPANY' | translate }}"
          filter="text"
          [hidden]="isHiddenColumn('company.name')"
        >
          <ng-template kendoGridFilterCellTemplate let-filter>
            <app-kendo-company-select [filter]="filter"></app-kendo-company-select>
          </ng-template>

          <ng-template kendoGridCellTemplate let-dataItem>
            <a [routerLink]="['/settings/companies/view']" [queryParams]="{ id: dataItem?.company_id }" target="_blank">
              {{ dataItem?.company?.shortName }}
            </a>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column
          field="sales_agent.fake_first_name"
          title="{{ 'CAMPAIGN.CAMPAIGN_LIST.SALES_AGENT_AND_SALES_MANAGER' | translate }}"
          filter="text"
          [hidden]="isHiddenColumn('sales_agent.fake_first_name')"
        >
          <ng-template kendoGridFilterCellTemplate let-filter>
            <app-kendo-user-select
              [filter]="filter"
              [roleType]="[UserRoleType.SALES_AGENT, UserRoleType.SALES_MANAGER]"
              [field]="'sales_agent_id'"
            ></app-kendo-user-select>
          </ng-template>

          <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
            {{ dataItem?.sales_agent?.fake_first_name }} {{ dataItem?.sales_agent?.fake_last_name }}
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column
          field="reservation_agent.fake_first_name"
          title="{{ 'CAMPAIGN.CAMPAIGN_LIST.RESERVATION_AGENT_AND_RESERVATION_MANAGER' | translate }}"
          filter="text"
          [hidden]="isHiddenColumn('reservation_agent.fake_first_name')"
        >
          <ng-template kendoGridFilterCellTemplate let-filter>
            <app-kendo-user-select
              [filter]="filter"
              [roleType]="[UserRoleType.RESERVATION_AGENT, UserRoleType.RESERVATION_MANAGER]"
              [field]="'reservation_agent_id'"
            ></app-kendo-user-select>
          </ng-template>

          <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
            {{ dataItem?.reservation_agent?.fake_first_name }} {{ dataItem?.reservation_agent?.fake_last_name }}
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column
          field="customer_care_agent.fake_first_name"
          title="{{ 'CAMPAIGN.CAMPAIGN_LIST.CUSTOMER_CARE_AGENT' | translate }}"
          filter="text"
          [hidden]="isHiddenColumn('customer_care_agent.fake_first_name', true)"
        >
          <ng-template kendoGridFilterCellTemplate let-filter>
            <app-kendo-user-select
              [filter]="filter"
              [roleType]="UserRoleType.CUSTOMER_CARE_AGENT"
              [field]="'customer_care_agent_id'"
            ></app-kendo-user-select>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column
          field="final_hotels"
          title="{{ 'CAMPAIGN.CAMPAIGN_LIST.FINAL_HOTELS' | translate }}"
          filter="text"
          [sortable]="false"
          [filterable]="false"
          [hidden]="isHiddenColumn('final_hotels', true)"
        >
          <ng-template kendoGridCellTemplate let-dataItem>
            {{ dataItem?.final_hotels }}
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column
          field="final_room_types"
          title="{{ 'CAMPAIGN.CAMPAIGN_LIST.FINAL_ROOM_TYPES' | translate }}"
          filter="text"
          [sortable]="false"
          [filterable]="false"
          [hidden]="isHiddenColumn('final_room_types', true)"
        >
          <ng-template kendoGridCellTemplate let-dataItem>
            {{ dataItem?.final_room_types }}
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column
          *ngIf="accessService.hasAccess([UserRoleType.EXTERNAL_PARTNER_LEAD], [], true)"
          width="140"
          field="rooms_count"
          title="{{ 'CAMPAIGN.CAMPAIGN_LIST.REQUESTED_ROOMS_COUNT' | translate }}"
          filter="numeric"
          [hidden]="isHiddenColumn('rooms_count', true)"
          format="0"
        >
          <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
            <div class="count-column-inner-custom">
              <small>{{ 'SALES.SALES_LIST.ALL' | translate }}</small>
              {{ dataItem.rooms_count }}
            </div>
            <div class="count-column-inner-custom center border-left">
              <small>{{ 'SALES.SALES_LIST.CLOSED' | translate }}</small>
              {{ dataItem.requestsClosedRoomsCount }}
            </div>
            <div class="count-column-inner-custom border-left">
              <small>{{ 'SALES.SALES_LIST.REST' | translate }}</small>
              {{ dataItem.rooms_count - dataItem.requestsClosedRoomsCount }}
            </div>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column
          *ngIf="accessService.hasAccess([UserRoleType.EXTERNAL_PARTNER_LEAD], [], true)"
          field="arrival_request.check_in"
          title="{{ 'CAMPAIGN.CAMPAIGN_LIST.FIRST_ARRIVAL_DATE' | translate }}"
          filter="date"
          format="{0:dd/MM/yyyy}"
          [hidden]="isHiddenColumn('arrival_request.check_in', true)"
        >
          <ng-template kendoGridFilterCellTemplate let-filter let-column="column">
            <kendo-grid-date-filter-cell
              [column]="column"
              [filter]="filter"
              operator="eq"
            ></kendo-grid-date-filter-cell>
          </ng-template>

          <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
            {{ dataItem.arrival_request?.check_in | date : env.pipeDateFormat }}
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column
          *ngIf="accessService.hasAccess([UserRoleType.EXTERNAL_PARTNER_LEAD], [], true)"
          field="departure_request.check_out"
          title="{{ 'CAMPAIGN.CAMPAIGN_LIST.FIRST_DEPARTURE_DATE' | translate }}"
          filter="date"
          format="{0:dd/MM/yyyy}"
          [hidden]="isHiddenColumn('departure_request.check_out', true)"
        >
          <ng-template kendoGridFilterCellTemplate let-filter let-column="column">
            <kendo-grid-date-filter-cell
              [column]="column"
              [filter]="filter"
              operator="eq"
            ></kendo-grid-date-filter-cell>
          </ng-template>

          <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
            {{ dataItem.departure_request?.check_out | date : env.pipeDateFormat }}
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column
          field="tags"
          title="{{ 'CAMPAIGN.CAMPAIGN_LIST.TAGS' | translate }}"
          filter="text"
          [hidden]="isHiddenColumn('tags', true)"
          [sortable]="false"
        >
          <ng-template kendoGridFilterCellTemplate let-filter let-column="column">
            <app-kendo-tag-select [filter]="filter" [model]="LaravelModelsEnum.SALE"></app-kendo-tag-select>
          </ng-template>

          <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
            <span *ngFor="let tag of dataItem?.tags; let i = index">
              {{ tag }}<span *ngIf="i !== dataItem?.tags?.length - 1">, </span>
            </span>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column
          field="channel.value"
          title="{{ 'CAMPAIGN.CAMPAIGN_LIST.CHANNEL' | translate }}"
          filter="text"
          [hidden]="isHiddenColumn('channel.value', true)"
        >
          <ng-template kendoGridFilterCellTemplate let-filter>
            <app-kendo-type-select
              [groupType]="'sale_channel'"
              [filter]="filter"
              [field]="'channel_id'"
            ></app-kendo-type-select>
          </ng-template>

          <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
            <app-type-badge [type]="dataItem.channel"></app-type-badge>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column
          field="campaign.value"
          title="{{ 'CAMPAIGN.CAMPAIGN_LIST.CAMPAIGN' | translate }}"
          filter="text"
          [hidden]="isHiddenColumn('campaign.value', true)"
        >
          <ng-template kendoGridFilterCellTemplate let-filter>
            <app-kendo-type-select
              [groupType]="'sale_campaign'"
              [filter]="filter"
              [field]="'campaign_id'"
            ></app-kendo-type-select>
          </ng-template>

          <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
            <app-type-badge [type]="dataItem.campaign"></app-type-badge>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column
          field="created_at"
          title="{{ 'CAMPAIGN.CAMPAIGN_LIST.CREATED_AT' | translate }}"
          filter="date"
          format="{0:dd/MM/yyyy}"
          [hidden]="isHiddenColumn('created_at', true)"
        >
          <ng-template kendoGridFilterCellTemplate let-filter let-column="column">
            <kendo-grid-date-filter-cell
              [column]="column"
              [filter]="filter"
              operator="eq"
            ></kendo-grid-date-filter-cell>
          </ng-template>
          <ng-template kendoGridCellTemplate let-dataItem>
            {{ dataItem.created_at.getDate() }}
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column
          *ngIf="accessService.hasAccess([UserRoleType.EXTERNAL_PARTNER_LEAD], [], true)"
          field="total_amount"
          title="{{ 'CAMPAIGN.CAMPAIGN_LIST.TOTAL_AMOUNT' | translate }}"
          filter="numeric"
          [hidden]="isHiddenColumn('total_amount')"
        >
          <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
            {{ dataItem.total_amount | number : '1.2-2' }} {{ 'DEFAULT_CURRENCY' | translate }}
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column
          *ngIf="accessService.hasAccess([UserRoleType.EXTERNAL_PARTNER_LEAD], [], true)"
          field="balance"
          title="{{ 'CAMPAIGN.CAMPAIGN_LIST.BALANCE' | translate }}"
          filter="numeric"
          [hidden]="isHiddenColumn('balance', true)"
        >
          <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
            <span
              class="font-weight-bold"
              [ngClass]="{ 'text-danger': dataItem?.balance < 0, 'text-success': dataItem?.balance >= 0 }"
            >
              {{ dataItem.balance | number : '1.2-2' }} {{ 'DEFAULT_CURRENCY' | translate }}
            </span>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column
          field="requests.status.value"
          title="{{ 'CAMPAIGN.CAMPAIGN_LIST.CLIENT_REQUESTS_STATUS' | translate }}"
          filter="text"
          [hidden]="isHiddenColumn('requests.status.value')"
          [sortable]="false"
        >
          <ng-template kendoGridFilterCellTemplate let-filter>
            <app-kendo-types-select
              [groupType]="'sale_request'"
              [filter]="filter"
              [field]="'requests.status_id'"
            ></app-kendo-types-select>
          </ng-template>

          <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
            <app-type-badge
              *ngFor="let status of dataItem?.requestsUniqueStatuses; let i = index"
              [type]="status"
              class="d-block"
            ></app-type-badge>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column
          *ngIf="accessService.hasAccess([UserRoleType.EXTERNAL_PARTNER_LEAD], [], true)"
          field="assign_to_user.fake_first_name"
          title="{{ 'CAMPAIGN.CAMPAIGN_LIST.ASSIGN_TO_USER' | translate }}"
          [hidden]="isHiddenColumn('assign_to_user.fake_first_name', true)"
        >
          <ng-template kendoGridFilterCellTemplate let-filter>
            <app-kendo-user-select [filter]="filter" [field]="'assign_to_user_id'"></app-kendo-user-select>
          </ng-template>

          <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
            <div>{{ dataItem?.assign_to_role?.name }}</div>
            <div>{{ dataItem?.assign_to_user?.names }}</div>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column
          *ngIf="accessService.hasAccess([UserRoleType.EXTERNAL_PARTNER_LEAD], [], true)"
          field="assign_date"
          title="{{ 'CAMPAIGN.CAMPAIGN_LIST.ASSIGN_DATE' | translate }}"
          filter="date"
          format="{0:dd/MM/yyyy}"
          [hidden]="isHiddenColumn('assign_date', true)"
        >
          <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
            {{ dataItem.assign_date | date : env.pipeDateFormat }}
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column
          title="{{ 'CAMPAIGN.CAMPAIGN_LIST.ACTIONS' | translate }}"
          [width]="100"
          [class]="'text-center'"
          [includeInChooser]="false"
        >
          <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
            <app-sale-button
              *ngIf="
                accessService.hasAccess(
                  [
                    UserRoleType.RESERVATION_AGENT,
                    UserRoleType.RESERVATION_MANAGER,
                    UserRoleType.CONTRACTING_AGENT,
                    UserRoleType.CONTRACTING_MANAGER,
                    UserRoleType.SALES_AGENT,
                    UserRoleType.SALES_MANAGER,
                    UserRoleType.CUSTOMER_CARE_AGENT,
                    UserRoleType.CUSTOMER_CARE_MANAGER,
                    UserRoleType.FINANCE_AGENT,
                    UserRoleType.FINANCE_MANAGER,
                    UserRoleType.LEAD_GENERATION,
                    UserRoleType.MARKETING,
                    UserRoleType.EXTERNAL_PARTNER_LEAD
                  ],
                  ['sales.show']
                )
              "
              [link]="['/sales/view']"
              [params]="{ id: dataItem.id }"
              [targetBlank]="true"
            >
            </app-sale-button>
          </ng-template>
        </kendo-grid-column>

        <ng-template kendoPagerTemplate let-totalPages="totalPages" let-currentPage="currentPage">
          <kendo-pager-prev-buttons></kendo-pager-prev-buttons>
          <kendo-pager-input></kendo-pager-input>
          <kendo-pager-info></kendo-pager-info>
          <kendo-pager-next-buttons></kendo-pager-next-buttons>
          <kendo-pager-page-sizes [pageSizes]="env.kendoGridPageSizes"></kendo-pager-page-sizes>
          <app-kendo-grid-refresh-button (click)="loadData(state)"></app-kendo-grid-refresh-button>
        </ng-template>
      </kendo-grid>
    </div>

    <div class="row align-items-center">
      <div class="col-auto">
        <h5 class="title-secondary">{{ 'CAMPAIGN.PAGE.TITLE_SELECTED' | translate }}</h5>
      </div>
    </div>

    <div class="bottom">
      <kendo-grid
        #selectedSalesGrid
        [style.height]="'100%'"
        [columnMenu]="false"
        [pageable]="false"
        [data]="selectedSales"
      >
        <kendo-grid-column
          [width]="70"
          class="text-center"
          headerClass="text-center"
          title="{{ 'CAMPAIGN.CAMPAIGN_LIST.NUMBER' | translate }}"
        >
          <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
            {{ rowIndex + 1 }}
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column
          class="text-center"
          headerClass="text-center"
          field="id"
          title="{{ 'CAMPAIGN.CAMPAIGN_LIST.ID' | translate }}"
          filter="numeric"
          [width]="120"
        ></kendo-grid-column>

        <kendo-grid-column
          class="text-center"
          headerClass="text-center"
          field="client.name"
          title="{{ 'CAMPAIGN.CAMPAIGN_LIST.CLIENT' | translate }}"
          filter="text"
        >
          <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
            <app-client-box [client]="dataItem.client" [flag]="true"></app-client-box>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column
          class="text-center"
          headerClass="text-center"
          field="event.formattedName"
          title="{{ 'CAMPAIGN.CAMPAIGN_LIST.EVENT' | translate }}"
          filter="text"
        >
          <ng-template kendoGridCellTemplate let-dataItem>
            <a [routerLink]="['/events/view']" [queryParams]="{ id: dataItem?.event?.id }" target="_blank">
              {{ dataItem?.event?.formattedName }}
            </a>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column
          class="text-center"
          headerClass="text-center"
          field="status.value"
          title="{{ 'CAMPAIGN.CAMPAIGN_LIST.STATUS' | translate }}"
          filter="text"
          [width]="120"
        >
          <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
            <app-type-badge [type]="dataItem.status"></app-type-badge>
            <div class="text-truncate" [ngbTooltip]="dataItem.status_reason" container="body">
              {{ dataItem.status_reason }}
            </div>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column
          field="company.name"
          title="{{ 'CAMPAIGN.CAMPAIGN_LIST.COMPANY' | translate }}"
          filter="text"
        >
          <ng-template kendoGridCellTemplate let-dataItem>
            <a [routerLink]="['/settings/companies/view']" [queryParams]="{ id: dataItem?.company_id }" target="_blank">
              {{ dataItem?.company?.shortName }}
            </a>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column
          field="sales_agent.fake_first_name"
          title="{{ 'CAMPAIGN.CAMPAIGN_LIST.SALES_AGENT_AND_SALES_MANAGER' | translate }}"
          filter="text"
        >
          <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
            {{ dataItem?.sales_agent?.fake_first_name }} {{ dataItem?.sales_agent?.fake_last_name }}
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column
          field="reservation_agent.fake_first_name"
          title="{{ 'CAMPAIGN.CAMPAIGN_LIST.RESERVATION_AGENT_AND_RESERVATION_MANAGER' | translate }}"
          filter="text"
        >
          <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
            {{ dataItem?.reservation_agent?.fake_first_name }} {{ dataItem?.reservation_agent?.fake_last_name }}
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column
          field="customer_care_agent.fake_first_name"
          title="{{ 'CAMPAIGN.CAMPAIGN_LIST.CUSTOMER_CARE_AGENT' | translate }}"
          filter="text"
        >
          <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
            {{ dataItem?.customer_care_agent?.fake_first_name }} {{ dataItem?.customer_care_agent?.fake_last_name }}
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column
          *ngIf="accessService.hasAccess([UserRoleType.EXTERNAL_PARTNER_LEAD], [], true)"
          field="total_amount"
          title="{{ 'CAMPAIGN.CAMPAIGN_LIST.TOTAL_AMOUNT' | translate }}"
          filter="numeric"
        >
          <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
            {{ dataItem.total_amount | number : '1.2-2' }} {{ 'DEFAULT_CURRENCY' | translate }}
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column
          class="text-center"
          headerClass="text-center"
          field="requests.status.value"
          title="{{ 'CAMPAIGN.CAMPAIGN_LIST.CLIENT_REQUESTS_STATUS' | translate }}"
          [filterable]="false"
          [sortable]="false"
          width="150"
        >
          <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
            <app-type-badge
              *ngFor="let status of dataItem?.requestsUniqueStatuses; let i = index"
              [type]="status"
              class="d-block"
            ></app-type-badge>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column
          class="text-center"
          headerClass="text-center"
          title="{{ 'OFFER_MULTIPLE_CLIENTS.ACTIONS' | translate }}"
          [width]="60"
          [class]="'text-center'"
        >
          <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
            <app-delete-button (click)="removeSelectedSale(dataItem)"></app-delete-button>
          </ng-template>
        </kendo-grid-column>
      </kendo-grid>
    </div>
  </div>

  <div class="flex-grow-1" *ngIf="currentStep === 2">
    <div class="filter-wrapper ps">
      <form [formGroup]="filterForm" novalidate (ngSubmit)="compile()" class="row">
        <div class="form-group col-sm-2">
          <label>{{ 'EMAIL_TEMPLATE_SENDER.TEMPLATE_TYPE' | translate }}</label>
          <ng-select
            [items]="template_types"
            bindLabel="name"
            bindValue="id"
            formControlName="template_type_id"
            (change)="loadTemplates($event)"
            appendTo="body"
          ></ng-select>
        </div>

        <div class="form-group col-sm-2">
          <label>{{ 'EMAIL_TEMPLATE_SENDER.COMPANY' | translate }}</label>
          <ng-select
            [items]="companies"
            bindLabel="shortName"
            bindValue="id"
            formControlName="company_id"
            (change)="filterTemplates()"
            appendTo="body"
          ></ng-select>
        </div>

        <div class="form-group col-sm-2">
          <label>{{ 'EMAIL_TEMPLATE_SENDER.LOCALE' | translate }}</label>
          <ng-select
            [items]="locales"
            bindLabel="name"
            bindValue="id"
            formControlName="locale_id"
            (change)="filterTemplates()"
            appendTo="body"
          ></ng-select>
        </div>

        <div class="form-group col-sm-2">
          <label>{{ 'EMAIL_TEMPLATE_SENDER.TEMPLATE' | translate }}</label>
          <ng-select
            [items]="filteredTemplates"
            formControlName="template_id"
            bindLabel="name"
            bindValue="id"
            appendTo="body"
          ></ng-select>
        </div>

        <div
          *ngIf="filterForm.get('template_type_id').value === templatesTypes.SALEWEBOFFERS || filterForm.get('template_type_id').value === templatesTypes.SALEAUTOCAMPAIGN"
          class="form-group col-sm-1 pt-4 pl-5 custom-checkbox"
        >
          <input class="custom-control-input" type="checkbox" formControlName="newest_wo" id="newest_wo_cb" />
          <label class="custom-control-label" for="newest_wo_cb">{{ 'CAMPAIGN.BUTTONS.NEWEST_WO' | translate }}</label>
        </div>

        <div
          *ngIf="filterForm.get('template_type_id').value === templatesTypes.SALEWEBOFFERS || filterForm.get('template_type_id').value === templatesTypes.SALEAUTOCAMPAIGN"
          class="form-group col-sm-1 pt-4 pl-5 custom-checkbox"
        >
          <input class="custom-control-input" type="checkbox" formControlName="latest_wo" id="latest_wo_cb" />
          <label class="custom-control-label" for="latest_wo_cb">{{ 'CAMPAIGN.BUTTONS.LATEST_WO' | translate }}</label>
        </div>
      </form>
    </div>

    <form
      class="email-content-wrapper flex-grow-1 overflow-auto row"
      id="email-template-sender-form"
      (ngSubmit)="sendEmail()"
      [formGroup]="form"
      novalidate
    >
      <div class="form-group col-sm-8">
        <app-event-select
          [parentFormGroup]="form"
          [onlyIncoming]="false"
          [onlyActive]="false"
          (modelChange)="changeEvent($event)"
          (select)="select($event)"
        ></app-event-select>
      </div>

      <div class="form-group col-sm-1">
        <button
          style="margin-top: 20px !important"
          type="button"
          class="btn btn-success mt-3"
          [disabled]="filterForm.get('template_id').invalid"
          (click)="compile()"
        >
          {{ 'EMAIL_TEMPLATE_SENDER.COMPILE' | translate }}
        </button>
      </div>

      <div class="form-group col-sm-1 pt-4 custom-checkbox">
        <input class="custom-control-input" type="checkbox" formControlName="add_sale" id="add_sale_cb" />
        <label class="custom-control-label" for="add_sale_cb">{{ 'CAMPAIGN.BUTTONS.ADD_SALE' | translate }}</label>
      </div>

      <div class="form-group col-sm-1 pt-4 custom-checkbox">
        <input class="custom-control-input" type="checkbox" formControlName="add_rl" id="add_rl_cb" />
        <label class="custom-control-label" for="add_rl_cb">{{ 'CAMPAIGN.BUTTONS.ADD_RL' | translate }}</label>
      </div>

      <div class="form-group col-sm-12">
        <kendo-grid
          #selectedSalesGrid2
          [style.height]="'100%'"
          [columnMenu]="false"
          [pageable]="false"
          [data]="stepTwoSelectedSales()"
        >
          <kendo-grid-column
            [width]="70"
            class="text-center"
            headerClass="text-center"
            title="{{ 'CAMPAIGN.CAMPAIGN_LIST.NUMBER' | translate }}"
            [width]="70"
          >
            <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
              <span *ngIf="dataItem && dataItem.client_id != undefined">
                {{ rowIndex + 1 }}
              </span>
            </ng-template>
          </kendo-grid-column>

          <kendo-grid-column
            class="text-center"
            headerClass="text-center"
            field="id"
            title="{{ 'CAMPAIGN.CAMPAIGN_LIST.ID' | translate }}"
            filter="numeric"
            [width]="120"
          ></kendo-grid-column>

          <kendo-grid-column
            class="text-center"
            headerClass="text-center"
            field="client.name"
            title="{{ 'CAMPAIGN.CAMPAIGN_LIST.CLIENT' | translate }}"
            filter="text"
          >
            <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
              <app-client-box
                *ngIf="dataItem && dataItem.client_id != undefined"
                [client]="dataItem.client"
                [flag]="true"
              ></app-client-box>
            </ng-template>
          </kendo-grid-column>

          <kendo-grid-column
            class="text-center"
            headerClass="text-center"
            field="event.formattedName"
            title="{{ 'CAMPAIGN.CAMPAIGN_LIST.EVENT' | translate }}"
            filter="text"
          >
            <ng-template kendoGridCellTemplate let-dataItem>
              <a
                *ngIf="dataItem && dataItem.event_id != undefined"
                [routerLink]="['/events/view']"
                [queryParams]="{ id: dataItem?.event?.id }"
                target="_blank"
              >
                {{ selectedEvent ? selectedEvent.formattedName : dataItem?.event?.formattedName }}
              </a>
            </ng-template>
          </kendo-grid-column>

          <kendo-grid-column
            class="text-center"
            headerClass="text-center"
            field="status.value"
            title="{{ 'CAMPAIGN.CAMPAIGN_LIST.STATUS' | translate }}"
            filter="text"
          >
            <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
              <app-type-badge
                *ngIf="dataItem && dataItem.status != undefined"
                [type]="dataItem.status"
              ></app-type-badge>
              <div
                *ngIf="dataItem && dataItem.status != undefined"
                class="text-truncate"
                [ngbTooltip]="dataItem.status_reason"
                container="body"
              >
                {{ dataItem.status_reason }}
              </div>
            </ng-template>
          </kendo-grid-column>

          <kendo-grid-column
            field="company.name"
            title="{{ 'CAMPAIGN.CAMPAIGN_LIST.COMPANY' | translate }}"
            filter="text"
          >
            <ng-template kendoGridCellTemplate let-dataItem>
              <a
                [routerLink]="['/settings/companies/view']"
                [queryParams]="{ id: dataItem?.company_id }"
                target="_blank"
              >
                {{ dataItem?.company?.shortName }}
              </a>
            </ng-template>
          </kendo-grid-column>

          <kendo-grid-column
            field="sales_agent.fake_first_name"
            title="{{ 'CAMPAIGN.CAMPAIGN_LIST.SALES_AGENT_AND_SALES_MANAGER' | translate }}"
            filter="text"
          >
            <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
              <div *ngIf="dataItem.note == 'mass'" class="text-center">
                <app-user-select
                  [parentFormGroup]="form"
                  [hideLabel]="true"
                  [passFormControlName]="'mass_assign_sales_agent_id'"
                  [roleType]="[UserRoleType.SALES_AGENT, UserRoleType.SALES_MANAGER]"
                  [isInModal]="false"
                ></app-user-select>
                <button type="button" class="btn btn-primary btn-ico" (click)="fillMassControls('sales_agent')">
                  <i class="fal fa-paper-plane"></i>
                </button>
              </div>
              <div *ngIf="dataItem.note != 'mass'">
                <app-user-select
                  [parentFormGroup]="form"
                  [hideLabel]="true"
                  [passFormControlName]="'requests_' + rowIndex + '_sales_agent_id'"
                  [roleType]="[UserRoleType.SALES_AGENT, UserRoleType.SALES_MANAGER]"
                  [unsubscribe]="false"
                  [isInModal]="false"
                ></app-user-select>
              </div>
            </ng-template>
          </kendo-grid-column>

          <kendo-grid-column
            field="reservation_agent.fake_first_name"
            title="{{ 'CAMPAIGN.CAMPAIGN_LIST.RESERVATION_AGENT_AND_RESERVATION_MANAGER' | translate }}"
            filter="text"
          >
            <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
              <div *ngIf="dataItem.note == 'mass'" class="text-center">
                <app-user-select
                  [parentFormGroup]="form"
                  [hideLabel]="true"
                  [passFormControlName]="'mass_assign_reservation_agent_id'"
                  [roleType]="[UserRoleType.RESERVATION_AGENT, UserRoleType.RESERVATION_MANAGER]"
                  [isInModal]="false"
                ></app-user-select>
                <button type="button" class="btn btn-primary btn-ico" (click)="fillMassControls('reservation_agent')">
                  <i class="fal fa-paper-plane"></i>
                </button>
              </div>
              <div *ngIf="dataItem.note != 'mass'">
                <app-user-select
                  [parentFormGroup]="form"
                  [hideLabel]="true"
                  [passFormControlName]="'requests_' + rowIndex + '_reservation_agent_id'"
                  [roleType]="[UserRoleType.RESERVATION_AGENT, UserRoleType.RESERVATION_MANAGER]"
                  [unsubscribe]="false"
                  [isInModal]="false"
                ></app-user-select>
              </div>
            </ng-template>
          </kendo-grid-column>

          <kendo-grid-column
            field="customer_care_agent.fake_first_name"
            title="{{ 'CAMPAIGN.CAMPAIGN_LIST.CUSTOMER_CARE_AGENT' | translate }}"
            filter="text"
          >
            <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
              <div *ngIf="dataItem.note == 'mass'" class="text-center">
                <app-user-select
                  [parentFormGroup]="form"
                  [hideLabel]="true"
                  [passFormControlName]="'mass_assign_customer_care_agent_id'"
                  [roleType]="[UserRoleType.CUSTOMER_CARE_AGENT]"
                  [isInModal]="false"
                ></app-user-select>
                <button type="button" class="btn btn-primary btn-ico" (click)="fillMassControls('customer_care_agent')">
                  <i class="fal fa-paper-plane"></i>
                </button>
              </div>
              <div *ngIf="dataItem.note != 'mass'">
                <app-user-select
                  [parentFormGroup]="form"
                  [hideLabel]="true"
                  [passFormControlName]="'requests_' + rowIndex + '_customer_care_agent_id'"
                  [roleType]="[UserRoleType.CUSTOMER_CARE_AGENT]"
                  [unsubscribe]="false"
                  [isInModal]="false"
                ></app-user-select>
              </div>
            </ng-template>
          </kendo-grid-column>

          <kendo-grid-column
            *ngIf="accessService.hasAccess([UserRoleType.EXTERNAL_PARTNER_LEAD], [], true)"
            field="total_amount"
            title="{{ 'CAMPAIGN.CAMPAIGN_LIST.TOTAL_AMOUNT' | translate }}"
            filter="numeric"
          >
            <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
              <span *ngIf="dataItem && dataItem.total_amount != undefined">
                {{ dataItem.total_amount | number : '1.2-2' }} {{ 'DEFAULT_CURRENCY' | translate }}
              </span>
            </ng-template>
          </kendo-grid-column>

          <kendo-grid-column
            class="text-center p-1"
            headerClass="text-center p-1"
            field="requests.status.value"
            title="{{ 'CAMPAIGN.CAMPAIGN_LIST.CLIENT_REQUESTS_STATUS' | translate }}"
            [filterable]="false"
            [sortable]="false"
          >
            <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
              <div *ngIf="dataItem && dataItem.requests != undefined">
                <app-type-badge
                  *ngFor="let status of dataItem?.requestsUniqueStatuses; let i = index"
                  [type]="status"
                  class="d-block"
                ></app-type-badge>
              </div>
            </ng-template>
          </kendo-grid-column>

          <!-- <kendo-grid-column
              class="text-center p-1"
              headerClass="text-center p-1"
              title="{{ 'OFFER_MULTIPLE_CLIENTS.ACTIONS' | translate }}"
              [width]="60"
              [class]="'text-center'"
            >
              <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
                <app-delete-button (click)="removeSelectedSale(dataItem)"></app-delete-button>
              </ng-template>
            </kendo-grid-column> -->
        </kendo-grid>
      </div>

      <div class="form-group col-sm-8">
        <div class="row">
          <div class="col-sm-12">
            <div class="form-group">
              <label>{{ 'EMAIL_TEMPLATE_SENDER.SUBJECT' | translate }}</label>
              <input type="text" class="form-control" formControlName="subject" />
            </div>
          </div>
        </div>
      </div>

      <div class="form-group col-sm-10">
        <div class="row">
          <div class="col-sm-12">
            <div class="form-group">
              <app-editor [form]="form" passFormControlName="body"></app-editor>
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>

  <div class="modal-footer d-flex justify-content-between">
    <div>
      <button *ngIf="currentStep === 2" (click)="setCurrentStep(1)" type="button" class="btn btn-outline-dark btn-xs">
        {{ 'CAMPAIGN.BUTTONS.PREV' | translate }}
      </button>
    </div>

    <div>
      <button
        *ngIf="currentStep === 1"
        (click)="setCurrentStep(2)"
        type="button"
        class="btn btn-outline-dark btn-xs"
        [disabled]="selectedSales.length <= 0"
      >
        {{ 'CAMPAIGN.BUTTONS.NEXT' | translate }}
      </button>

      <div class="d-flex" *ngIf="currentStep === 2">
        <button
          type="button"
          form="email-template-sender-form"
          [disabled]="form.invalid"
          class="btn btn-outline-dark btn-xs ml-2"
          (click)="sendEmail()"
        >
          {{ 'CAMPAIGN.BUTTONS.SEND' | translate }}
        </button>
      </div>
    </div>
  </div>
</div>
